import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Mail, Phone } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Clients() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ id: null, cliente_id: '', cliente_nome: '', cliente_email: '', cliente_telefone: '', cliente_endereco: '' });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`);
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    // Auto generate a cliente_id if empty on POST
    const payload = { ...formData };
    if (!payload.id && !payload.cliente_id) {
        payload.cliente_id = 'CLI' + Math.floor(Math.random() * 10000);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchClients();
      } else {
        showAlert('Aviso','Erro ao salvar cliente');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleEdit = (client) => {
    setFormData({ 
      id: client.id, 
      cliente_id: client.cliente_id, 
      cliente_nome: client.cliente_nome, 
      cliente_email: client.cliente_email, 
      cliente_telefone: client.cliente_telefone, 
      cliente_endereco: client.cliente_endereco 
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este cliente?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchClients();
      } else {
        showAlert('Aviso','Erro ao excluir cliente');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData({ id: null, cliente_id: '', cliente_nome: '', cliente_email: '', cliente_telefone: '', cliente_endereco: '' });
    setIsModalOpen(true);
  };

  const filteredClients = clients.filter(client => 
    (client.cliente_nome && client.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.cliente_email && client.cliente_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.cliente_telefone && client.cliente_telefone.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Gerencie o cadastro de clientes da empresa</p>
        </div>
        <button className="btn-primary" onClick={openNewModal}>
          <Plus size={18} />
          Novo Cliente
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar cliente por nome, email ou telefone..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Contato</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="font-medium">{client.cliente_nome}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>ID: {client.cliente_id} (Ref: #{client.id})</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <Mail size={14} /> {client.cliente_email}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <Phone size={14} /> {client.cliente_telefone}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-success">Ativo</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => handleEdit(client)}><Edit2 size={16} /></button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => handleDelete(client.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Cliente" : "Novo Cliente"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div className="form-group flex-2">
            <label>Nome ou Razão Social</label>
            <input type="text" name="cliente_nome" value={formData.cliente_nome} onChange={handleInputChange} placeholder="Ex: Empresa Exemplo Ltda" required className="standard-input" />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div className="form-group flex-1">
              <label>E-mail Corporativo</label>
              <input type="email" name="cliente_email" value={formData.cliente_email} onChange={handleInputChange} placeholder="contato@empresa.com" required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>Telefone</label>
              <input type="tel" name="cliente_telefone" value={formData.cliente_telefone} onChange={handleInputChange} placeholder="(00) 0000-0000" className="standard-input" />
            </div>
          </div>
          <div className="form-group">
            <label>Endereço Completo</label>
            <input type="text" name="cliente_endereco" value={formData.cliente_endereco} onChange={handleInputChange} placeholder="Rua, Número, Bairro, Cidade - UF" className="standard-input" />
          </div>
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-success">Salvar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
